import React from 'react';

import useCurrentTurnProperty from '../hooks/useCurrentTurnProperty';
import useCurrentTurnRef from '../hooks/useCurrentTurnRef';
import usePlayerProperty from '../hooks/usePlayerProperty';
import usePlayerRef from '../hooks/usePlayerRef';
import useUserId from '../hooks/useUserId';
import { Card as CardType } from '../shared/firebaseTypes';
import { Character, Response } from '../shared/types';
import { getHasValue, getLabelForCharacter } from '../utils';
import ActionResolution from './ActionResolution';
import Card from './Card';
import Counteraction from './Counteraction';
import Flexbox from './Flexbox';
import Message from './Message';
import MessageButton from './MessageButton';
import MessageContainer from './MessageContainer';
import PendingMessage from './PendingMessage';
import PlayerName from './PlayerName';

type Props = {
  hasCounteractions: boolean;
};

const ActionChallengeResolution: React.FC<Props> = ({ hasCounteractions }) => {
  // Firebase data
  const userId = useUserId();
  const actionChallengeResolution = useCurrentTurnProperty(
    'actionChallengeResolution',
  );
  const actionResponseByUserId = useCurrentTurnProperty(
    'actionResponseByUserId',
  );
  const influenceCards = usePlayerProperty(userId, 'influenceCards');

  // Firebase ref
  const currentTurnRef = useCurrentTurnRef();
  const playerRef = usePlayerRef(userId);

  // Derrived data
  const hasActionChallengeResolution = getHasValue(actionChallengeResolution);
  const challengingPlayerUserId = Object.entries(
    actionResponseByUserId || {},
  ).find((entry) => entry[1] === Response.Challenge)?.[0] as string;
  const isChallengingPlayer = challengingPlayerUserId === userId;

  if (hasActionChallengeResolution) {
    return (
      <>
        <MessageContainer key="resolved" userId={challengingPlayerUserId}>
          <Flexbox flexDirection="row" width="100%">
            <Message>
              <span>
                <strong>
                  <PlayerName userId={challengingPlayerUserId} />
                </strong>{' '}
                revealed{' '}
                <strong>
                  {getLabelForCharacter(actionChallengeResolution as Character)}
                </strong>
              </span>
            </Message>
          </Flexbox>
          <Card character={actionChallengeResolution as Character} isRevealed />
        </MessageContainer>
        {hasCounteractions ? <Counteraction /> : <ActionResolution />}
      </>
    );
  }

  if (isChallengingPlayer) {
    return (
      <MessageContainer key="resolving" userId={userId}>
        <Flexbox flexDirection="row" width="100%">
          <Message>
            <span>
              <strong>
                <PlayerName userId={userId} />
              </strong>{' '}
              revealed…
            </span>
          </Message>
        </Flexbox>
        {Object.entries(influenceCards || {}).map(
          ([key, card]: [string, CardType], i) =>
            !card.isRevealed ? (
              <MessageButton
                key={i}
                onClick={() => {
                  currentTurnRef
                    .child('actionChallengeResolution')
                    .set(card.character);
                  playerRef
                    .child('influenceCards')
                    .child(key)
                    .set({
                      ...card,
                      isRevealed: true,
                    });
                }}>
                {getLabelForCharacter(card.character as Character)}
              </MessageButton>
            ) : null,
        )}
      </MessageContainer>
    );
  }

  return (
    <MessageContainer userId={challengingPlayerUserId}>
      <PendingMessage />
    </MessageContainer>
  );
};

export default ActionChallengeResolution;
